import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { ClerkProvider, useAuth as useClerkAuth } from "@clerk/clerk-react";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { BaseProvider } from "@/contexts/BaseContext";
import AppShell from "@/components/layout/AppShell";
import LoginPage from "@/pages/LoginPage";
import DashboardPage from "@/pages/DashboardPage";
import ChangesPage from "@/pages/ChangesPage";
import HorsesPage from "@/pages/HorsesPage";
import HorseDetailPage from "@/pages/HorseDetailPage";
import RankingsPage from "@/pages/RankingsPage";
import AssistantPage from "@/pages/AssistantPage";
import SeasonsPage from "@/pages/SeasonsPage";
import SalariesPage from "@/pages/SalariesPage";
import StaffPage from "@/pages/StaffPage";
import FeedInventoryPage from "@/pages/FeedInventoryPage";
import TackInventoryPage from "@/pages/TackInventoryPage";
import VehiclesPage from "@/pages/VehiclesPage";
import InvoicesPage from "@/pages/InvoicesPage";
import WhatsAppConsolePage from "@/pages/WhatsAppConsolePage";
import WhatsAppEditorPage from "@/pages/WhatsAppEditorPage";
import AuditPage from "@/pages/AuditPage";
import SettingsPage from "@/pages/SettingsPage";
import MembersAndRolesPage from "@/pages/MembersAndRolesPage";
import BreedingPage from "@/pages/BreedingPage";
import RatingsMigrationPage from "@/pages/RatingsMigrationPage";
import CostsPage from "@/pages/CostsPage";
import ConnectorPage from "@/pages/ConnectorPage";
import TaskConfirmPage from "@/pages/TaskConfirmPage";
import TasksPage from "@/pages/TasksPage";
import LocationsPage from "@/pages/LocationsPage";
import BasesOverviewPage from "@/pages/BasesOverviewPage";
import { resolveHomePath } from "@/lib/home-path";
import PrivacyPolicyPage from "@/pages/legal/PrivacyPolicyPage";
import TermsPage from "@/pages/legal/TermsPage";
import DataDeletionPage from "@/pages/legal/DataDeletionPage";
import PublicHorseReportPage from "@/pages/PublicHorseReportPage";
import "@/App.css";

const CLERK_PUBLISHABLE_KEY = process.env.REACT_APP_CLERK_PUBLISHABLE_KEY;

// Swallow benign unhandled rejections (axios 401 retries, navigation aborts, hot-reload)
if (typeof window !== "undefined" && !window.__ap_rej_handler) {
  window.__ap_rej_handler = true;
  window.addEventListener("unhandledrejection", (event) => {
    const r = event?.reason;
    const status = r?.response?.status;
    const msg = (r?.message || r?.toString?.() || "").toLowerCase();
    // 401 is already handled by axios interceptor → don't show overlay
    if (status === 401 || status === 403) { event.preventDefault(); return; }
    // navigation/aborted / network errors during hot reload
    if (msg.includes("canceled") || msg.includes("aborted") || msg.includes("network error")) {
      event.preventDefault();
    }
  });
}

function Protected({ children }) {
  const { isSignedIn, isLoaded } = useClerkAuth();
  const location = useLocation();

  if (!isLoaded) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="text-sm text-muted-foreground">Loading…</div>
      </div>
    );
  }

  // Clerk is the single source of truth for sign-in state. If Clerk says the
  // user is not signed in → redirect to the Clerk-hosted /login page. If
  // Clerk says they are signed in → render the app. We do NOT show any
  // custom "access pending" / "access denied" screen — Clerk's own UI
  // (restrictions, invitations, etc.) handles those cases.
  if (!isSignedIn) {
    return <Navigate to={`/login?next=${encodeURIComponent(location.pathname)}`} replace />;
  }

  return children;
}

/**
 * Route guard that requires `isAdmin`. Non-admin signed-in users
 * are redirected to the dashboard root. Used to keep executives out
 * of WhatsApp, Audit, and Settings pages.
 */
function AdminRoute({ children }) {
  const { isAdmin, loading } = useAuth();
  if (loading) {
    return (
      <div className="flex h-[60vh] items-center justify-center">
        <div className="text-sm text-muted-foreground">Loading…</div>
      </div>
    );
  }
  if (!isAdmin) return <Navigate to="/" replace />;
  return children;
}

/**
 * Route guard that requires the master_admin flag.
 */
function MasterRoute({ children }) {
  const { isMaster, loading } = useAuth();
  if (loading) {
    return <div className="flex h-[60vh] items-center justify-center"><div className="text-sm text-muted-foreground">Loading…</div></div>;
  }
  if (!isMaster) return <Navigate to="/" replace />;
  return children;
}

/**
 * Route guard that requires a specific permission key.
 * Waits for the permissions fetch to resolve before making a redirect
 * decision — otherwise `hasPerm(perm)` reads the empty initial Set
 * and Navigates the user away from a page they legitimately own,
 * causing a redirect loop that trips history.replaceState throttling.
 *
 * If the user lacks the requested perm we redirect to `/`, which is
 * handled by `HomeRoute` — that component picks the first accessible
 * sidebar page for the user (or renders an explicit "No access" panel
 * if nothing is accessible). This means unauthorized routes never
 * dead-end and never infinite-loop.
 */
function PermRoute({ perm, children }) {
  const { hasPerm, loading, permissionsLoaded } = useAuth();
  if (loading || !permissionsLoaded) {
    return <div className="flex h-[60vh] items-center justify-center"><div className="text-sm text-muted-foreground">Loading…</div></div>;
  }
  if (!hasPerm(perm)) {
    return <Navigate to="/" replace />;
  }
  return children;
}

/**
 * Landing-page router for `/`. Sends the user to the first sidebar
 * entry their role/permissions allow (see `resolveHomePath`). This
 * prevents restricted users (grooms, viewers, etc.) from being greeted
 * with a "No access" screen right after sign-in. If the resolved home
 * IS `/`, we render the dashboard directly. If the user has literally
 * zero accessible pages we render the explicit no-access panel — that
 * also breaks any potential Navigate-loop.
 */
function HomeRoute() {
  const { hasPerm, isAdmin, isMaster, loading, permissionsLoaded } = useAuth();
  if (loading || !permissionsLoaded) {
    return <div className="flex h-[60vh] items-center justify-center"><div className="text-sm text-muted-foreground">Loading…</div></div>;
  }
  const home = resolveHomePath({ hasPerm, isAdmin, isMaster });
  if (home === "/") return <DashboardPage />;
  if (home) return <Navigate to={home} replace />;
  return (
    <div className="flex h-[60vh] items-center justify-center p-8" data-testid="home-no-access">
      <div className="max-w-md text-center space-y-3">
        <div className="text-lg font-medium text-foreground">No access</div>
        <p className="text-sm text-muted-foreground">
          Your account doesn't have permission to view any pages on this dashboard yet.
          Contact your administrator to request access.
        </p>
      </div>
    </div>
  );
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/login/*" element={<LoginPage />} />
      <Route path="/sign-up/*" element={<Navigate to="/login" replace />} />
      {/* Public legal pages — no Clerk login required */}
      <Route path="/privacy" element={<PrivacyPolicyPage />} />
      <Route path="/terms" element={<TermsPage />} />
      <Route path="/data-deletion" element={<DataDeletionPage />} />
      {/* Public horse-report share link — no Clerk login required */}
      <Route path="/share/horse/:token" element={<PublicHorseReportPage />} />
      <Route element={<Protected><AppShell /></Protected>}>
        <Route path="/" element={<HomeRoute />} />
        <Route path="/changes" element={<PermRoute perm="dashboard:read"><ChangesPage /></PermRoute>} />
        <Route path="/horses" element={<PermRoute perm="horses:read"><HorsesPage /></PermRoute>} />
        <Route path="/horses/rankings" element={<PermRoute perm="rankings:read"><RankingsPage /></PermRoute>} />
        <Route path="/trends" element={<PermRoute perm="assistant:use"><AssistantPage /></PermRoute>} />
        <Route path="/assistant" element={<PermRoute perm="assistant:use"><AssistantPage /></PermRoute>} />
        <Route path="/seasons" element={<PermRoute perm="trends:read"><SeasonsPage /></PermRoute>} />
        <Route path="/horses/:id" element={<PermRoute perm="horses:read"><HorseDetailPage /></PermRoute>} />
        <Route path="/staff" element={<PermRoute perm="staff:read"><StaffPage /></PermRoute>} />
        <Route path="/inventory/feed" element={<PermRoute perm="inventory:read"><FeedInventoryPage /></PermRoute>} />
        <Route path="/inventory/tack" element={<PermRoute perm="inventory:read"><TackInventoryPage /></PermRoute>} />
        <Route path="/vehicles" element={<PermRoute perm="inventory:read"><VehiclesPage /></PermRoute>} />
        <Route path="/invoices" element={<PermRoute perm="invoices:read"><InvoicesPage /></PermRoute>} />
        <Route path="/salaries" element={<PermRoute perm="salaries:read"><SalariesPage /></PermRoute>} />
        <Route path="/breeding" element={<PermRoute perm="breeding:read"><BreedingPage /></PermRoute>} />
        <Route path="/whatsapp" element={<AdminRoute><WhatsAppConsolePage /></AdminRoute>} />
        <Route path="/whatsapp-editor" element={<AdminRoute><WhatsAppEditorPage /></AdminRoute>} />
        <Route path="/audit" element={<AdminRoute><AuditPage /></AdminRoute>} />
        <Route path="/settings" element={<AdminRoute><SettingsPage /></AdminRoute>} />
        <Route path="/horses/import-ratings" element={<PermRoute perm="horses:write"><RatingsMigrationPage /></PermRoute>} />
        <Route path="/members" element={<MasterRoute><MembersAndRolesPage /></MasterRoute>} />
        <Route path="/costs" element={<PermRoute perm="costs:read"><CostsPage /></PermRoute>} />
        <Route path="/connector" element={<PermRoute perm="assistant:use"><ConnectorPage /></PermRoute>} />
        <Route path="/connector/task" element={<PermRoute perm="dashboard:read"><TaskConfirmPage /></PermRoute>} />
        <Route path="/tasks" element={<PermRoute perm="dashboard:read"><TasksPage /></PermRoute>} />
        <Route path="/locations" element={<PermRoute perm="locations:manage"><LocationsPage /></PermRoute>} />
        <Route path="/bases/overview" element={<PermRoute perm="dashboard:read"><BasesOverviewPage /></PermRoute>} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  if (!CLERK_PUBLISHABLE_KEY) {
    return (
      <div className="flex h-screen items-center justify-center p-8">
        <div className="max-w-md text-sm text-destructive">
          Missing <code>REACT_APP_CLERK_PUBLISHABLE_KEY</code> in frontend/.env. restart the frontend after adding it.
        </div>
      </div>
    );
  }
  return (
    <ClerkProvider publishableKey={CLERK_PUBLISHABLE_KEY} afterSignOutUrl="/login">
      <BrowserRouter>
        <AuthProvider>
          <BaseProvider>
            <AppRoutes />
            <Toaster richColors position="top-right" />
          </BaseProvider>
        </AuthProvider>
      </BrowserRouter>
    </ClerkProvider>
  );
}

export default App;
